import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { categoryImage } from "../../lib/categories";
import { toast } from "sonner";

const empty = { name: "", slug: "", description: "", image_url: "", parent_slug: "", is_active: true, sort_order: 0 };

const slugify = (v) => v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing };
      if (!payload.slug) payload.slug = slugify(payload.name);
      if (editing.id) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Enregistré ✿");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (c) => {
    const children = cats.filter((x) => x.parent_slug === c.slug);
    if (children.length > 0) {
      toast.error(`Impossible : ${children.length} sous-catégorie(s) sont rattachées à "${c.name}"`);
      return;
    }
    if (!window.confirm(`Supprimer la catégorie "${c.name}" ?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    load();
  };

  // Strip server-only fields from a payload before PUT
  const stripForPut = (c) => {
    const { id, _id, depth, orphan, ...rest } = c;
    return rest;
  };

  // Move a category up or down among its siblings (same parent_slug)
  const move = async (c, dir) => {
    const siblings = cats
      .filter((x) => (x.parent_slug || "") === (c.parent_slug || ""))
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    // Normalize sort_orders to 0..N (avoids ties)
    siblings.forEach((s, i) => { s._idx = i; });
    const me = siblings.find((s) => s.id === c.id);
    const target = dir === "up" ? siblings[me._idx - 1] : siblings[me._idx + 1];
    if (!target) return; // edge of list
    try {
      await Promise.all([
        api.put(`/admin/categories/${me.id}`, { ...stripForPut(me), sort_order: target._idx }),
        api.put(`/admin/categories/${target.id}`, { ...stripForPut(target), sort_order: me._idx }),
      ]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de réordonnancement");
    }
  };

  // Build a hierarchical, flat-rendered list: parents first, then children indented.
  const ordered = useMemo(() => {
    const parents = cats.filter((c) => !c.parent_slug).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const result = [];
    for (const p of parents) {
      result.push({ ...p, depth: 0 });
      const children = cats
        .filter((c) => c.parent_slug === p.slug)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      for (const c of children) result.push({ ...c, depth: 1 });
    }
    // catch orphans (parent_slug references an unknown parent)
    const seenIds = new Set(result.map((r) => r.id));
    for (const c of cats) if (!seenIds.has(c.id)) result.push({ ...c, depth: 0, orphan: true });
    return result;
  }, [cats]);

  // Available parents = top-level categories, minus the one being edited (no self-parenting)
  const parentOptions = useMemo(
    () => cats.filter((c) => !c.parent_slug && (!editing?.id || c.id !== editing.id)),
    [cats, editing]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline text-berulia-rosedeep">BERULIA</p>
          <h1 className="font-heading text-3xl sm:text-4xl mt-2">
            Catégories <span className="font-serif-italic text-xl text-berulia-inkmuted">& sous-catégories</span>
          </h1>
        </div>
        <button onClick={() => setEditing(empty)} data-testid="new-cat-btn" className="bg-berulia-ink text-berulia-beige overline px-6 py-3">
          + Nouvelle catégorie
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white border border-berulia-border p-6 mb-8 grid md:grid-cols-2 gap-4">
          <label>
            <span className="overline">Nom</span>
            <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: editing.slug || slugify(e.target.value) })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
          </label>
          <label>
            <span className="overline">Slug (URL)</span>
            <input required value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
          </label>

          <label>
            <span className="overline">Catégorie parente</span>
            <select
              value={editing.parent_slug || ""}
              onChange={(e) => setEditing({ ...editing, parent_slug: e.target.value })}
              className="mt-1 w-full border border-berulia-border px-3 py-2 bg-white"
              data-testid="cat-parent"
            >
              <option value="">— Catégorie principale —</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.slug}>{p.name}</option>
              ))}
            </select>
            <span className="text-[11px] text-berulia-inkmuted italic block mt-1">
              Laissez vide pour une catégorie de niveau racine.
            </span>
          </label>

          <label>
            <span className="overline">Ordre d'affichage</span>
            <input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value || "0", 10) })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
          </label>

          <label className="md:col-span-2">
            <span className="overline">Description</span>
            <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full border border-berulia-border px-3 py-2" />
          </label>

          <label className="md:col-span-2">
            <span className="overline">Image URL</span>
            <input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="mt-1 w-full border border-berulia-border px-3 py-2" placeholder="https://..." />
          </label>

          <label className="flex items-center gap-2">
            <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
            <span className="text-sm">Active (visible côté boutique)</span>
          </label>

          <div className="md:col-span-2 flex gap-3 pt-2">
            <button type="button" onClick={() => setEditing(null)} className="overline px-5 py-2 border border-berulia-border">Annuler</button>
            <button type="submit" data-testid="cat-save" className="bg-berulia-ink text-berulia-beige overline px-6 py-2">Enregistrer</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-berulia-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-berulia-beigewarm">
            <tr>
              <th className="text-left p-3 w-12">Tri</th>
              <th className="text-left p-3 w-16">Image</th>
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-left p-3">Parente</th>
              <th className="text-left p-3">Ordre</th>
              <th className="text-left p-3">Active</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map((c) => {
              const siblings = cats
                .filter((x) => (x.parent_slug || "") === (c.parent_slug || ""))
                .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
              const idx = siblings.findIndex((s) => s.id === c.id);
              const isFirst = idx === 0;
              const isLast = idx === siblings.length - 1;
              return (
                <tr key={c.id} className={`border-t border-berulia-border ${c.depth === 1 ? "bg-berulia-cream/40" : ""}`}>
                  <td className="p-3">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => move(c, "up")}
                        disabled={isFirst}
                        title="Monter"
                        className={`w-7 h-6 border border-berulia-border bg-white text-sm ${isFirst ? "opacity-30 cursor-not-allowed" : "hover:bg-berulia-cream"}`}
                      >↑</button>
                      <button
                        onClick={() => move(c, "down")}
                        disabled={isLast}
                        title="Descendre"
                        className={`w-7 h-6 border border-berulia-border bg-white text-sm ${isLast ? "opacity-30 cursor-not-allowed" : "hover:bg-berulia-cream"}`}
                      >↓</button>
                    </div>
                  </td>
                  <td className="p-3">
                    {(() => {
                      const img = categoryImage(c);
                      return img ? (
                        <img src={img} alt="" className="w-10 h-10 object-contain bg-berulia-beigewarm" onError={(e) => { e.target.style.visibility = "hidden"; }} />
                      ) : (
                        <div className="w-10 h-10 bg-berulia-beigewarm" />
                      );
                    })()}
                  </td>
                  <td className="p-3">
                    {c.depth === 1 && <span className="text-berulia-inkmuted mr-2">└─</span>}
                    <span className={c.depth === 0 ? "font-heading text-base" : ""}>{c.name}</span>
                    {c.orphan && <span className="ml-2 text-[10px] text-red-700/70 uppercase tracking-widest">orphelin</span>}
                  </td>
                  <td className="p-3 overline">{c.slug}</td>
                  <td className="p-3 overline text-berulia-inkmuted">{c.parent_slug || "—"}</td>
                  <td className="p-3">{c.sort_order ?? 0}</td>
                  <td className="p-3">{c.is_active ? "✓" : "—"}</td>
                  <td className="p-3 text-right space-x-3">
                    <button onClick={() => setEditing(c)} className="overline link-underline">Éditer</button>
                    <button onClick={() => remove(c)} className="overline text-red-700/70">Supprimer</button>
                  </td>
                </tr>
              );
            })}
            {ordered.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-berulia-inkmuted">Aucune catégorie. Créez-en une pour commencer.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
